/**
 * Ozonetel CloudAgent MDL — client session + server proxy
 *
 * DigitalConsole SDK handles calls; hold/mute require a separate CloudAgent MDL session
 * (usId, agentUniqId) from auth_service/login — see iframeHttpsCurls at repo root.
 *
 * Browsers cannot POST to mdlConnection.php directly (CORS). This service:
 * - Stores mdlSession locally (memory + localStorage)
 * - Syncs session to the Moneytor server
 * - Sends hold/mute/unhold/unmute via POST /api/ozonetel/cloudagent/mdl/*
 *   (server calls Ozonetel with curl-equivalent headers)
 * - Inbound auto-answer step 1: POST /api/ozonetel/cloudagent/toolbar/pick-inbound
 *   (server GET getCustomerInfo — screen-pop only; SDK must sipAccept() for audio)
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Fields required on every mdlConnection.php payload (iframe parity). */
export interface CloudagentMdlSession {
  customer: string;
  agentId: string;
  usId: string;
  agentUniqId: number;
  browserSessionId: string;
  utid: number;
}

type MdlOp = 'hold' | 'unhold' | 'mute' | 'unmute';

@Injectable({ providedIn: 'root' })
export class OzonetelCloudagentDirectService {
  private sess: CloudagentMdlSession | null = null;

  constructor(private http: HttpClient) {}

  get session(): CloudagentMdlSession | null {
    return this.sess;
  }

  /** Clears in-memory session and removes persisted copy for this agent. */
  clearSession(): void {
    if (this.sess?.customer && this.sess.agentId) {
      try {
        localStorage.removeItem(this.storageKey(this.sess.customer, this.sess.agentId));
      } catch {
      }
    }
    this.sess = null;
  }

  private storageKey(customer: string, agentId: string): string {
    return `ozonetel_mdl_${customer}_${agentId}`;
  }

  /**
   * Restore session after page refresh. Used before hold/mute if init already ran once.
   */
  loadPersistedSession(customer: string, agentId: string): boolean {
    try {
      const raw = localStorage.getItem(this.storageKey(customer, agentId));
      if (!raw) {
        return false;
      }
      const p = JSON.parse(raw) as CloudagentMdlSession;
      if (!p?.usId || p.agentUniqId == null) {
        return false;
      }
      this.setSession({
        customer: p.customer || customer,
        agentId: p.agentId || agentId,
        usId: p.usId,
        agentUniqId: p.agentUniqId,
        browserSessionId: p.browserSessionId,
        utid: p.utid,
      });
      return true;
    } catch {
      return false;
    }
  }

  persistSession(): void {
    if (!this.sess?.customer || !this.sess.agentId) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey(this.sess.customer, this.sess.agentId), JSON.stringify(this.sess));
    } catch {
    }
  }

  /**
   * Apply session from server login, SDK hints, or profile. Also syncs to server for mdl proxy.
   */
  setSession(partial: Partial<CloudagentMdlSession> & { usId: string; agentUniqId: number }): void {
    this.sess = {
      customer: String(partial.customer ?? this.sess?.customer ?? ''),
      agentId: String(partial.agentId ?? this.sess?.agentId ?? ''),
      usId: String(partial.usId),
      agentUniqId: Number(partial.agentUniqId),
      browserSessionId: partial.browserSessionId || this.sess?.browserSessionId || this.newBrowserSessionId(),
      utid: Number(partial.utid) || this.sess?.utid || Math.floor(Math.random() * 50000) + 1,
    };
    this.persistSession();
    void this.syncSessionToServer();
  }

  hasSession(): boolean {
    return !!(this.sess?.usId && this.sess.agentUniqId != null);
  }

  private newBrowserSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  /** Legacy check; MDL apikey is applied on the server via OZONETEL_API_KEY. */
  resolveApiKey(): boolean {
    const key = (environment as { ozonetel_mdl_api_key?: string }).ozonetel_mdl_api_key;
    return !!(key && key !== '1');
  }

  /** Maps Ozonetel MDL type strings to Moneytor route segments. */
  private mdlRoute(type: string): MdlOp | null {
    const map: Record<string, MdlOp> = {
      tbHold: 'hold',
      tbUnHold: 'unhold',
      tbMute: 'mute',
      tbUnMute: 'unmute',
    };
    return map[type] || null;
  }

  /**
   * Matches Ozonetel toolbar JSON (not MDL): type "response", custData[], or "" === "success".
   * Mirrors server toolbarResponseLooksOk so client accepts ok even if route parsing differs.
   */
  private toolbarPickOk(data: unknown): boolean {
    if (data == null) {
      return false;
    }
    let body: Record<string, unknown> | string = data as string;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body) as Record<string, unknown>;
      } catch {
        return false;
      }
    }
    if (body[''] === 'success' || body[''] === 'Success') {
      return true;
    }
    const st = String(body['status'] ?? body['resp'] ?? body['result'] ?? '').toLowerCase();
    if (st === 'success' || st === 'true') {
      return true;
    }
    if (String(body['type'] ?? '').toLowerCase() === 'response') {
      return Array.isArray(body['custData']) || body['custData'] != null;
    }
    return Array.isArray(body['custData']) && body['custData'].length > 0;
  }

  private mdlOk(data: unknown): boolean {
    if (data == null) {
      return false;
    }
    let body: Record<string, unknown> | string = data as string;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body) as Record<string, unknown>;
      } catch {
        return false;
      }
    }
    const resp = String(body['resp'] ?? body['status'] ?? body['result'] ?? '').toLowerCase();
    if (resp === 'success' || resp === 'true') {
      return true;
    }
    if (body['success'] === true) {
      return true;
    }
    return false;
  }

  /** Keeps server-side session map in sync when client already has usId/agentUniqId. */
  async syncSessionToServer(): Promise<boolean> {
    if (!this.sess) {
      return false;
    }
    try {
      const res: { resultShort?: string } = await firstValueFrom(
        this.http.post(`${environment.api_url}/api/ozonetel/cloudagent/mdl/sync-session`, {
          mdlSession: this.sess,
        })
      );
      return res?.resultShort === 'success';
    } catch {
      return false;
    }
  }

  /**
   * Proxy hold/mute to server. Body includes call fields (ucid, did, …) + full mdlSession.
   * Server POSTs to https://in1-ccaas-api.ozonetel.com/.../mdlConnection.php (no CORS).
   */
  async postMdl(
    type: string,
    extra: Record<string, unknown>
  ): Promise<{ ok: boolean; message?: string; data?: unknown }> {
    if (!this.sess) {
      return { ok: false, message: 'CloudAgent mdl session missing' };
    }
    const route = this.mdlRoute(type);
    if (!route) {
      return { ok: false, message: `Unsupported mdl type: ${type}` };
    }
    this.sess.utid += 1;
    try {
      const res: {
        resultShort?: string;
        resultLong?: string;
        data?: unknown;
        httpStatus?: number;
      } = await firstValueFrom(
        this.http.post(`${environment.api_url}/api/ozonetel/cloudagent/mdl/${route}`, {
          ...extra,
          mdlSession: { ...this.sess },
        })
      );
      const data = res?.data;
      if (res?.resultShort === 'success' || this.mdlOk(data)) {
        this.persistSession();
        return { ok: true, data };
      }
      return {
        ok: false,
        message: res?.resultLong || 'mdl proxy reported failure',
        data,
      };
    } catch (e: unknown) {
      const err = e as { error?: { resultLong?: string; message?: string } };
      return {
        ok: false,
        message:
          err?.error?.resultLong ||
          err?.error?.message ||
          (e instanceof Error ? e.message : String(e ?? 'mdl request failed')),
      };
    }
  }

  /**
   * Iframe-equivalent GET getCustomerInfo (tbGetCustomerData) via Moneytor server.
   * extra: params (caller digits), monitorUcid, callType "false", ucid.
   * Always POSTs so Network tab shows pick-inbound; server holds CloudAgent cookies.
   */
  async toolbarPickInbound(
    extra: Record<string, unknown>,
    identity?: { customer: string; agentId: string }
  ): Promise<{ ok: boolean; message?: string; data?: unknown }> {
    const body: Record<string, unknown> = { ...extra };
    if (this.sess) {
      body['mdlSession'] = { ...this.sess };
    } else if (identity?.customer && identity?.agentId) {
      body['customer'] = identity.customer;
      body['agentId'] = identity.agentId;
    }
    const url = `${environment.api_url}/api/ozonetel/cloudagent/toolbar/pick-inbound`;
    console.log('[OzonetelCloudagentDirect] POST toolbar/pick-inbound', {
      params: body['params'],
      monitorUcid: body['monitorUcid'],
      hasMdlSession: !!body['mdlSession'],
    });
    try {
      const res: {
        resultShort?: string;
        resultLong?: string;
        data?: unknown;
        httpStatus?: number;
      } = await firstValueFrom(this.http.post(url, body));
      const data = res?.data;
      if (res?.resultShort === 'success' || this.toolbarPickOk(data)) {
        return { ok: true, data };
      }
      return {
        ok: false,
        message: res?.resultLong || 'toolbar getCustomerInfo did not return custData',
        data,
      };
    } catch (e: unknown) {
      const err = e as { error?: { resultLong?: string; message?: string } };
      return {
        ok: false,
        message:
          err?.error?.resultLong ||
          err?.error?.message ||
          (e instanceof Error ? e.message : String(e ?? 'toolbar pick failed')),
      };
    }
  }

  /** MDL tbAgentLogout via server, then clear local session. */
  async logout(phoneNumber: string): Promise<void> {
    if (this.sess) {
      try {
        await firstValueFrom(
          this.http.post(`${environment.api_url}/api/ozonetel/cloudagent/logout`, {
            phoneNumber: phoneNumber || '',
            mdlSession: { ...this.sess },
          })
        );
      } catch {
      }
    }
    this.clearSession();
  }
}
